import React, { useState, useEffect } from 'react';

function FilterPopup({ 
    isOpen, 
    onClose, 
    optimizedRoutes, 
    activeFilters, 
    onFiltersChange 
}) {
    const [selectedDepartments, setSelectedDepartments] = useState(activeFilters.departments || []);
    const [selectedDistricts, setSelectedDistricts] = useState(activeFilters.districts || []);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState(activeFilters.taskTypes || []);
    const [selectedPriorities, setSelectedPriorities] = useState(activeFilters.priorities || []);
    const [selectedSchedulingMethods, setSelectedSchedulingMethods] = useState(activeFilters.schedulingMethods || []);
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

    // Generate available filter options from optimizedRoutes (for dynamic filters)
    const availableDepartments = [...new Set(
        optimizedRoutes.map(route => route.assignedInspector.INSPECTOR_DEPARTMENT)
    )].sort();

    const availableDistricts = [...new Set(
        optimizedRoutes.flatMap(route => route.assignedInspector.districtCodes || [])
    )].sort((a, b) => parseInt(a) - parseInt(b));

    // Predefined options for the three new filters (always show these options)
    const availableTaskTypes = ['inspection', 'reinspection'];
    const availablePriorities = ['A', 'B', 'B+', 'C', 'D'];
    const availableSchedulingMethods = ['Pending', 'Scheduled'];

    // Reset local state when popup opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedDepartments(activeFilters.departments || []);
            setSelectedDistricts(activeFilters.districts || []);
            setSelectedTaskTypes(activeFilters.taskTypes || []);
            setSelectedPriorities(activeFilters.priorities || []);
            setSelectedSchedulingMethods(activeFilters.schedulingMethods || []);
            // Reset dropdown states when popup opens
            setShowDepartmentDropdown(false);
            setShowDistrictDropdown(false);
        }
    }, [isOpen, activeFilters]);

    const handleDepartmentToggle = (department) => {
        setSelectedDepartments(prev => 
            prev.includes(department)
                ? prev.filter(d => d !== department)
                : [...prev, department]
        );
    };

    const handleDistrictToggle = (district) => {
        setSelectedDistricts(prev => 
            prev.includes(district)
                ? prev.filter(d => d !== district)
                : [...prev, district]
        );
    };

    const handleTaskTypeToggle = (taskType) => {
        setSelectedTaskTypes(prev => 
            prev.includes(taskType)
                ? prev.filter(t => t !== taskType)
                : [...prev, taskType]
        );
    };

    const handlePriorityToggle = (priority) => {
        setSelectedPriorities(prev => 
            prev.includes(priority)
                ? prev.filter(p => p !== priority)
                : [...prev, priority]
        );
    };

    const handleSchedulingMethodToggle = (method) => {
        setSelectedSchedulingMethods(prev => 
            prev.includes(method)
                ? prev.filter(m => m !== method)
                : [...prev, method]
        );
    };

    const removeDepartment = (department) => {
        setSelectedDepartments(prev => prev.filter(d => d !== department));
    };

    const removeDistrict = (district) => {
        setSelectedDistricts(prev => prev.filter(d => d !== district));
    };

    const clearAllFilters = () => {
        setSelectedDepartments([]);
        setSelectedDistricts([]);
        setSelectedTaskTypes([]);
        setSelectedPriorities([]);
        setSelectedSchedulingMethods([]);
        // Immediately apply the cleared filters and close the popup
        onFiltersChange({
            departments: [],
            districts: [],
            taskTypes: [],
            priorities: [],
            schedulingMethods: []
        });
        onClose();
    };

    const applyFilters = () => {
        onFiltersChange({
            departments: selectedDepartments,
            districts: selectedDistricts,
            taskTypes: selectedTaskTypes,
            priorities: selectedPriorities,
            schedulingMethods: selectedSchedulingMethods
        });
        onClose();
    };

    // Helper function to get display name for scheduling method
    const getSchedulingMethodDisplayName = (status) => {
        switch (status) {
            case 'Pending': return 'Auto-scheduled';
            case 'Scheduled': return 'Manually scheduled';
            default: return status;
        }
    };

    // Helper function to get display name for task type
    const getTaskTypeDisplayName = (taskType) => {
        switch (taskType) {
            case 'inspection': return 'Inspection';
            case 'reinspection': return 'Re-inspection';
            default: return taskType;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-2/3 h-4/5 flex flex-col" style={{ zIndex: 1000 }}>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-xl font-semibold text-myblue-900">Filter Inspectors</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                    {/* Inspector-based Filters Section */}
                    <div className="space-y-6">
                        {/* Inspector Department Filter */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                                Inspector Department
                            </label>
                            <div className="relative">
                                <button
                                    onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                                    className="w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-xs"
                                >
                                    <span className={selectedDepartments.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                                        {selectedDepartments.length > 0 
                                            ? `${selectedDepartments.length} selected`
                                            : 'Select departments...'
                                        }
                                    </span>
                                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                
                                {showDepartmentDropdown && (
                                    <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto" style={{ zIndex: 99999 }}>
                                        {availableDepartments.map(department => (
                                            <label key={department} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDepartments.includes(department)}
                                                    onChange={() => handleDepartmentToggle(department)}
                                                    className="mr-2"
                                                />
                                                <span className="text-xs">{department}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected Department Tags */}
                            {selectedDepartments.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedDepartments.map(department => (
                                        <span
                                            key={department}
                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                            <span className="text-xs">{department}</span>
                                            <button
                                                onClick={() => removeDepartment(department)}
                                                className="ml-1 text-blue-600 hover:text-blue-800 text-xs"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* District Codes Filter */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                                Community District Codes
                            </label>
                            <div className="relative">
                                <button
                                    onClick={() => setShowDistrictDropdown(!showDistrictDropdown)}
                                    className="w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-xs"
                                >
                                    <span className={selectedDistricts.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                                        {selectedDistricts.length > 0 
                                            ? `${selectedDistricts.length} selected`
                                            : 'Select districts...'
                                        }
                                    </span>
                                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                
                                {showDistrictDropdown && (
                                    <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto" style={{ zIndex: 99999 }}>
                                        {availableDistricts.map(district => (
                                            <label key={district} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDistricts.includes(district)}
                                                    onChange={() => handleDistrictToggle(district)}
                                                    className="mr-2"
                                                />
                                                <span className="text-xs">{district}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected District Tags */}
                            {selectedDistricts.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedDistricts.map(district => (
                                        <span
                                            key={district}
                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                        >
                                            <span className="text-xs">{district}</span>
                                            <button
                                                onClick={() => removeDistrict(district)}
                                                className="ml-1 text-green-600 hover:text-green-800 text-xs"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Inspection-based Filters Section */}
                    <div className="space-y-6">
                        {/* Section Header */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-sm font-medium text-myblue-900 mb-4">
                                Assigned inspections that contain...
                            </h4>
                        </div>

                        {/* Task Type Filter - Button Based */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                                Task Type
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {availableTaskTypes.map(taskType => (
                                    <button
                                        key={taskType}
                                        onClick={() => handleTaskTypeToggle(taskType)}
                                        className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                                            selectedTaskTypes.includes(taskType)
                                                ? 'bg-purple-100 text-purple-800 border-purple-300'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {getTaskTypeDisplayName(taskType)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Priority Filter - Button Based */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                                Priority Level
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {availablePriorities.map(priority => (
                                    <button
                                        key={priority}
                                        onClick={() => handlePriorityToggle(priority)}
                                        className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                                            selectedPriorities.includes(priority)
                                                ? 'bg-red-100 text-red-800 border-red-300'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        Priority {priority}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Scheduling Method Filter - Button Based */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">
                                Scheduling Method
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {availableSchedulingMethods.map(method => (
                                    <button
                                        key={method}
                                        onClick={() => handleSchedulingMethodToggle(method)}
                                        className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                                            selectedSchedulingMethods.includes(method)
                                                ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {getSchedulingMethodDisplayName(method)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between bg-transparent p-6 border-t border-gray-200 flex-shrink-0">
                    <button
                        onClick={clearAllFilters}
                        className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800"
                    >
                        Clear Filters
                    </button>
                    <div className="space-x-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={applyFilters}
                            className="px-4 py-2 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FilterPopup; 